import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FiAlertTriangle, FiEye, FiPlus, FiSearch, FiShoppingBag, FiTrash2 } from 'react-icons/fi';
import './AdminUI.css';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('All');
  const navigate = useNavigate();

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to view orders');
        navigate('/');
        return;
      }

      const response = await axios.get('https://valleyapi-dr9y.onrender.com/api/cart/admin/orders', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setOrders(response.data);
      setFilteredOrders(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.message || 'Failed to load orders');
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [navigate]);

  useEffect(() => {
    let result = orders;
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          order.companyName.toLowerCase().includes(lowerSearch) ||
          order.idorders.toString().includes(lowerSearch)
      );
    }
    if (selectedState !== 'All') {
      result = result.filter((order) => order.order_state === selectedState);
    }
    setFilteredOrders(result);
  }, [searchTerm, selectedState, orders]);

  const handleDeleteClick = (idorders) => {
    setOrderToDelete(idorders);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to delete orders');
        navigate('/');
        return;
      }

      await axios.delete(`https://valleyapi-dr9y.onrender.com/api/cart/admin/orders/${orderToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setError('');
      setShowDeleteModal(false);
      setOrderToDelete(null);
      await fetchOrders();
    } catch (err) {
      console.error('Error deleting order:', err);
      setError(err.response?.data?.message || 'Failed to delete order');
      setShowDeleteModal(false);
      setOrderToDelete(null);
    }
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
  };

  const handleCreateOrder = () => {
    navigate('/admin/orders/create');
  };

  const money = (value) => `$${Number(value || 0).toFixed(2)}`;
  const stateClass = (state) => {
    if (['Delivered', 'Shipped'].includes(state)) return 'va-badge--success';
    if (['Cancelled'].includes(state)) return 'va-badge--danger';
    if (['Waiting for Payment', 'Quote Requested'].includes(state)) return 'va-badge--warning';
    return 'va-badge--info';
  };

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Operations</span>
          <h1 className="va-title">Orders</h1>
          <p className="va-subtitle">Track quotes and orders from request through delivery.</p>
        </div>
        <div className="va-header-actions">
          <button onClick={handleCreateOrder} className="va-button"><FiPlus /> Create order</button>
        </div>
      </header>

      <div className="va-toolbar">
        <div className="va-toolbar__group">
          <div className="va-search">
            <FiSearch />
            <input type="search" placeholder="Search company or order number" value={searchTerm} onChange={handleSearchChange} aria-label="Search orders" />
          </div>
          <select value={selectedState} onChange={handleStateChange} className="va-select" aria-label="Filter by order status">
            <option value="All">All statuses</option>
            <option value="Quote Requested">Quote Requested</option>
            <option value="Quote Submitted">Quote Submitted</option>
            <option value="Waiting for Payment">Waiting for Payment</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        <span className="va-results">{filteredOrders.length} result{filteredOrders.length === 1 ? '' : 's'}</span>
      </div>

      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}
      {filteredOrders.length === 0 && !error ? (
        <div className="va-card va-empty"><div><FiShoppingBag /><strong>No orders found</strong><p>Try changing your search or status filter.</p></div></div>
      ) : (
        <div className="va-table-wrap">
          <table className="va-table">
            <thead>
              <tr>
                <th>Order</th><th>Company</th><th>Status</th><th>Subtotal</th><th>Shipping</th><th>Taxes</th><th>Total</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.idorders}>
                  <td><span className="va-table__primary">#{order.idorders}</span></td>
                  <td>{order.companyName}</td>
                  <td><span className={`va-badge ${stateClass(order.order_state)}`}>{order.order_state}</span></td>
                  <td className="va-number">{money(order.subtotal)}</td>
                  <td className="va-number">{money(order.shipping_cost)}</td>
                  <td className="va-number">{money(order.taxes)}</td>
                  <td className="va-number"><strong>{money(order.total)}</strong></td>
                  <td className="va-number">{new Date(order.date).toLocaleDateString()}</td>
                  <td><div className="va-table__actions">
                    <Link to={`/admin/orders/${order.idorders}`} className="va-button va-button--ghost va-button--icon" title="View order" aria-label={`View order ${order.idorders}`}><FiEye /></Link>
                    <button onClick={() => handleDeleteClick(order.idorders)} className="va-button va-button--ghost va-button--icon" title="Delete order" aria-label={`Delete order ${order.idorders}`}><FiTrash2 /></button>
                  </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showDeleteModal && (
        <div className="va-modal-overlay" role="presentation" onMouseDown={closeModal}>
          <div className="va-modal" role="dialog" aria-modal="true" aria-labelledby="delete-order-title" onMouseDown={(event) => event.stopPropagation()}>
            <span className="va-modal__icon"><FiTrash2 /></span>
            <h3 id="delete-order-title">Delete order #{orderToDelete}?</h3>
            <p>This order and its associated information will be permanently removed. This action cannot be undone.</p>
            <div className="va-modal__actions">
              <button onClick={closeModal} className="va-button va-button--secondary">Cancel</button>
              <button onClick={handleDelete} className="va-button va-button--danger">Delete order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
